import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { CaseServices } from "../../../services/CaseServices";
import EditIcon from "../../../assets/editIcon.svg";
import DeleteIcon from "../../../assets/deleteIcon.svg";
import CloseIcon from "../../../assets/crossIcon.svg";
import CustomPagination from "../../../utils/paginationUtils";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import DocumentNameSelect from "./DocumentNameSelect";
const DOC_TYPE_OPTIONS = [
  { value: "ADMISSIONDOCUMENT", label: "ADMISSION DOCUMENT" },
  { value: "DURINGTREATMENTDOCUMENT", label: "DURING TREATMENT DOCUMENT" },
  { value: "ONDISCHARGEDOCUMENT", label: "ON DISCHARGE DOCUMENT" },
];

const CASE_TYPE_OPTIONS = [
  { value: "SURGICAL", label: "SURGICAL" },
  { value: "CONSERVATIVE", label: "CONSERVATIVE" },
  { value: "DAYCARE", label: "DAYCARE" },
];

const AddDefaultDocument = ({ setOpen }) => {
  // form state
  const [documentName, setDocumentName] = useState("");
  const [documentType, setDocumentType] = useState(""); // form value
  const [caseType, setCaseType] = useState(""); // form value
  const [submitting, setSubmitting] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);

  // list + pagination
  const [defaultDocs, setDefaultDocs] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(0);

  // filters (separate from form)
  const [filterDocType, setFilterDocType] = useState("");
  const [filterCaseType, setFilterCaseType] = useState("");

  const formRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // ---- API: fetch list (filters + pagination) ----
  const fetchDocuments = async () => {
    try {
      const res = await CaseServices.defaultDocumentsWithoutGroup({
        page,
        limit,
        caseType: filterCaseType || "",
        documentType: filterDocType || "",
        searchKey: "",
        procedureId: "",
        specialityId: "",
        implants: "",
        stratification: "",
      });

      setDefaultDocs(res?.data?.list || []);
      setTotalPages(res?.data?.totalPages || 0);
    } catch (err) {
      toast.error("Failed to load documents");
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterDocType, filterCaseType]);

  // ---- actions ----
  const handleEditClick = (doc) => {
    setEditingDocId(doc.id);
    setDocumentName(doc.documentName || "");
    setDocumentType(doc.documentType || "");
    setCaseType(doc.caseType || "");

    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const handleCancelUpdate = () => {
    setDocumentName("");
    setDocumentType("");
    setCaseType("");
    setEditingDocId(null);

    setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const handleDelete = async (doc) => {
    const ok = window.confirm(
      `Are you sure you want to delete the document "${doc.documentName}"?`
    );
    if (!ok) return;

    try {
      await CaseServices.removeDefaultDocument([doc.id]);
      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (err) {
      console.error("Error while deleting document:", err);
      toast.error("Failed to delete document");
    }
  };

  const handleSaveDocument = async () => {
    if (
      !documentName ||
      !documentType ||
      (!caseType && documentType !== "DURINGTREATMENTDOCUMENT")
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    const payloadItem = {
      documentName,
      documentType,
      caseType:
        documentType === "DURINGTREATMENTDOCUMENT" ? null : caseType || null,
    };

    if (editingDocId) payloadItem.id = editingDocId;

    try {
      setSubmitting(true);
      await CaseServices.addDefaultDocuments([payloadItem]);
      toast.success(
        editingDocId
          ? "Document updated successfully"
          : "Document added successfully"
      );
      await fetchDocuments();
      setDocumentName("");
      setDocumentType("");
      setCaseType("");
      setEditingDocId(null);
    } catch (err) {
      toast.error("Failed to save document");
    } finally {
      setSubmitting(false);
    }
  };

  const docTypeValueObj = documentType
    ? DOC_TYPE_OPTIONS.find((o) => o.value === documentType)
    : null;

  const caseTypeValueObj = caseType
    ? CASE_TYPE_OPTIONS.find((o) => o.value === caseType)
    : null;

  const filterDocTypeValueObj = filterDocType
    ? DOC_TYPE_OPTIONS.find((o) => o.value === filterDocType)
    : null;

  const filterCaseTypeValueObj = filterCaseType
    ? CASE_TYPE_OPTIONS.find((o) => o.value === filterCaseType)
    : null;

  return (
    <div className="fixed inset-0 top-[54px] flex justify-center items-center bg-[#017C5780] z-20 px-4">
      <div className="relative bg-white rounded-[16px] p-4 sm:p-6 md:p-8 w-full max-w-[75%] max-h-[75vh] overflow-auto">
        <div className="absolute  flex items-center gap-2  right-8 top-4 cursor-pointer ">
          <img
            src={CloseIcon}
            alt="close-icon"
            onClick={() => setOpen(false)}
          />
        </div>

        {/* ====== FORM ====== */}
        <div ref={formRef} className="bg-[#F9F9F9] p-4 rounded-[8px] mt-5">
          <div className="grid grid-cols-1 xl:grid-cols-2 mt-4 gap-4">
            <div>
              <div className="font-semibold mb-2">Document Name</div>
              <DocumentNameSelect
                onChange={setDocumentName}
                value={documentName}
              />
            </div>
            {/* <div>
              <div className="font-semibold mb-2">Document Name</div>
              <input
                type="text"
                placeholder="Enter Document Name"
                style={{ border: "1px solid #E4E4E4" }}
                className="w-full text-sm py-[8px] px-[12px] rounded-[8px]"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
              />
            </div> */}
            <div>
              <div className="font-semibold mb-2">Document Type</div>
              <Select
                name="documentType"
                options={DOC_TYPE_OPTIONS}
                value={docTypeValueObj}
                onChange={(item) => {
                  setDocumentType(item?.value || "");
                  setCaseType("");
                }}
              />
            </div>
            <div>
              <div className="font-semibold mb-2">Case Type</div>
              <Select
                name="caseType"
                isDisabled={documentType === "DURINGTREATMENTDOCUMENT"}
                options={CASE_TYPE_OPTIONS}
                value={caseTypeValueObj}
                onChange={(item) => setCaseType(item?.value || "")}
              />
            </div>
          </div>
        </div>

        {/* Save / Cancel */}
        <div className="flex justify-center items-center mt-4 gap-4">
          <button
            className="text-sm text-white border-[#017C57] bg-[#017C57] font-semibold px-5 py-2 rounded-full cursor-pointer disabled:opacity-60"
            onClick={handleSaveDocument}
            disabled={submitting}
          >
            {editingDocId ? "Update Document" : "Save Document"}
          </button>

          {editingDocId && (
            <button
              className="text-sm text-[#017C57] border border-[#017C57] font-semibold px-5 py-2 rounded-full cursor-pointer"
              onClick={handleCancelUpdate}
            >
              Cancel Update
            </button>
          )}
        </div>

        {/* ===== Filters ===== */}
        <div className="flex flex-wrap items-center gap-4 mt-6 mb-4">
          <div className="w-60">
            <Select
              placeholder="Filter by Document Type"
              options={DOC_TYPE_OPTIONS}
              value={filterDocTypeValueObj}
              onChange={(item) => {
                setPage(1);
                setFilterDocType(item?.value || "");
              }}
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                  height: "40px",
                  width: "100%", // forces full width
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: "14px",
                  color: "#6b7280",
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: "40px",
                  padding: "0 8px",
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: "40px",
                }),
              }}
            />
          </div>

          <div className="w-60">
            <Select
              placeholder="Filter by Case Type"
              options={CASE_TYPE_OPTIONS}
              value={filterCaseTypeValueObj}
              onChange={(item) => {
                setPage(1);
                setFilterCaseType(item?.value || "");
              }}
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                  height: "40px",
                  width: "100%", // same width as above
                }),
                placeholder: (base) => ({
                  ...base,
                  fontSize: "14px", // 👈 smaller font size
                  color: "#6b7280", // optional: Tailwind gray-500
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: "40px",
                  padding: "0 8px",
                }),
                input: (base) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: "40px",
                }),
              }}
            />
          </div>
        </div>

        {/* ====== LIST ====== */}
        <div ref={listRef} className="mt-2 max-h-[500px] overflow-visible">
          {defaultDocs?.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Document Name
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Document Type
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Case Type
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">
                    Edit
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {defaultDocs.map((doc, idx) => (
                  <tr key={doc.id || idx}>
                    <td className="px-4 py-2 text-sm text-gray-800">
                      {doc.documentName}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-800">
                      {doc.documentType}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-800">
                      {doc.caseType}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleEditClick(doc)}
                      >
                        <img
                          src={EditIcon}
                          alt="edit-icon"
                          width={20}
                          height={20}
                          className="inline-block hover:opacity-80"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div
                        className="cursor-pointer"
                        onClick={() => handleDelete(doc)}
                      >
                        <img
                          src={DeleteIcon}
                          alt="delete-icon"
                          width={20}
                          height={20}
                          className="inline-block hover:opacity-80"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-sm italic">No documents found.</p>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-3">
          <CustomPagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AddDefaultDocument;
